export interface PricelistCaseVendor {
  companyId: string;
  companyName: string;
  /**
   * GEN-3074: the specific vendor#s the case is for under this company.
   * Empty or absent means every vendor# the company carries (company-level),
   * which is how every case behaved before.
   */
  vendorNumbers?: readonly string[];
}

/** One pickable row: a company, at one of its vendor#s when it has any. */
export interface PricelistCaseVendorOption {
  companyId: string;
  companyName: string;
  vendorNumber: string | null;
}

/** One sidebar chip: a company-level vendor, or one explicit vendor#. */
export type PricelistCaseVendorChip = PricelistCaseVendorOption;

function explicitNumbers(vendor: PricelistCaseVendor): readonly string[] {
  return vendor.vendorNumbers ?? [];
}

export function pricelistCaseVendorChips(
  vendors: readonly PricelistCaseVendor[],
): PricelistCaseVendorChip[] {
  return vendors.flatMap((vendor): PricelistCaseVendorChip[] => {
    const numbers = explicitNumbers(vendor);
    if (numbers.length === 0) {
      return [{ ...companyOf(vendor), vendorNumber: null }];
    }
    return numbers.map((vendorNumber) => ({
      ...companyOf(vendor),
      vendorNumber,
    }));
  });
}

function companyOf(vendor: PricelistCaseVendor) {
  return { companyId: vendor.companyId, companyName: vendor.companyName };
}

/** A stable id for one picker row / chip: company plus vendor#. */
export function pricelistCaseVendorOptionId(
  option: PricelistCaseVendorOption,
): string {
  return `${option.companyId}:${option.vendorNumber ?? ''}`;
}

/**
 * The vendor set a list of picked rows describes (GEN-3074). Rows group by
 * company in first-seen order. A company with any picked vendor# is explicit
 * — picking 61500 for a company-level vendor narrows it to 61500 — and one
 * picked only at company level keeps every vendor#. A company whose last
 * vendor# was unpicked is simply absent: it leaves the case rather than
 * widening back to "every vendor#".
 */
export function pricelistCaseVendorsFromOptions(
  options: readonly PricelistCaseVendorOption[],
): PricelistCaseVendor[] {
  const vendors: PricelistCaseVendor[] = [];
  for (const option of options) {
    const existing = vendors.find(
      (vendor) => vendor.companyId === option.companyId,
    );
    const added = option.vendorNumber;
    if (existing === undefined) {
      vendors.push({
        ...companyOf(option),
        vendorNumbers: added === null ? [] : [added],
      });
      continue;
    }
    const current = explicitNumbers(existing);
    if (added !== null && !current.includes(added)) {
      existing.vendorNumbers = [...current, added];
    }
  }
  return vendors;
}

/**
 * A chip's ×: the vendor set without that chip. `keepLast` refuses to remove
 * the last chip — a saved case always has a vendor.
 */
export function removePricelistCaseVendorChip(
  vendors: readonly PricelistCaseVendor[],
  chip: PricelistCaseVendorChip,
  keepLast: boolean,
): PricelistCaseVendor[] {
  const chips = pricelistCaseVendorChips(vendors);
  if (keepLast && chips.length <= 1) return [...vendors];
  const removedId = pricelistCaseVendorOptionId(chip);
  return pricelistCaseVendorsFromOptions(
    chips.filter((item) => pricelistCaseVendorOptionId(item) !== removedId),
  );
}

/** What `cases.update` takes for a vendor set. */
export function pricelistCaseVendorsUpdate(
  vendors: readonly PricelistCaseVendor[],
): { companyIds: string[]; vendorNumbers: string[] } {
  return {
    companyIds: pricelistCaseVendorIds(vendors),
    vendorNumbers: vendors.flatMap((vendor) => [...explicitNumbers(vendor)]),
  };
}

/** Same companies and vendor#s, in the same order. */
export function samePricelistCaseVendors(
  left: readonly PricelistCaseVendor[],
  right: readonly PricelistCaseVendor[],
): boolean {
  return (
    JSON.stringify(pricelistCaseVendorsUpdate(left)) ===
    JSON.stringify(pricelistCaseVendorsUpdate(right))
  );
}

export function pricelistCaseVendorIds(
  vendors: readonly PricelistCaseVendor[],
): string[] {
  return vendors.map((vendor) => vendor.companyId);
}

export function formatPricelistCaseVendorNames(
  vendors: readonly PricelistCaseVendor[],
  formatName: (name: string) => string = (name) => name,
): string {
  return vendors.map((vendor) => formatName(vendor.companyName)).join(' / ');
}

export function caseVendorsFromLegacy(
  companyId: string,
  companyName: string,
  vendors?: readonly PricelistCaseVendor[],
): PricelistCaseVendor[] {
  if (vendors !== undefined && vendors.length > 0) {
    return [...vendors];
  }
  return [{ companyId, companyName }];
}

export function pricelistCaseVendorsMatchSearch(
  vendors: readonly PricelistCaseVendor[],
  needle: string,
): boolean {
  const term = needle.trim().toLowerCase();
  if (term === '') return true;
  return vendors.some((vendor) =>
    vendor.companyName.toLowerCase().includes(term),
  );
}

const COMPANY_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function companyIdsFromAuditValue(value: unknown): string[] | null {
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    return COMPANY_ID_PATTERN.test(value) ? [value] : null;
  }
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return [];
  if (
    !value.every(
      (item) => typeof item === 'string' && COMPANY_ID_PATTERN.test(item),
    )
  ) {
    return null;
  }
  return value;
}

export function pricelistCaseVendorNameById(
  vendors: readonly PricelistCaseVendor[],
  names: Readonly<Record<string, string>>,
): Map<string, string> {
  const map = new Map<string, string>(Object.entries(names));
  for (const vendor of vendors) {
    map.set(vendor.companyId, vendor.companyName);
  }
  return map;
}

export function formatPricelistCaseVendorAuditValue(
  value: unknown,
  nameById: ReadonlyMap<string, string>,
  formatName: (name: string) => string,
  emptyLabel: string,
): string | null {
  const ids = companyIdsFromAuditValue(value);
  if (ids === null) return null;
  if (ids.length === 0) return emptyLabel;
  return ids.map((id) => formatName(nameById.get(id) ?? id)).join(' / ');
}
